// @flow
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import history from 'utils/history';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import OutsideClickHandler from 'react-outside-click-handler';
import styles from './styles.module.scss';
import routes from '../../constants/routes';
import Logo from '../../components/Logo/Logo';
import CustomLink from '../../components/CustomLink/Link';
import Burger from '../../components/Burger';
import MobileMenu from '../../components/MobileMenu';
import profileIcon from '../../assets/icons/profile.svg';
import ProfileMenu from '../../components/ProfileMenu';
import actionTypes from '../../store/auth/actionTypes';
import { LinkButton } from '../../components';
import { menuLinks } from '../../db/links';
import LanguageSelect from '../../components/LanguageSelect';
import useWindowDimensions from '../../utils/windowDemensions';
import PloSwitch from 'components/PloSwitch/ploswitch';

type Props = {
  className?: string,
}
const Header = ({ className }: Props) => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const tutorialItemName = useSelector((state) => state.tutorial.name);
  const isLoggedIn = useSelector((state) => state.auth.isAuthenticated);
  const [isMenuVisible, setMenuVisible] = useState<boolean>(false);
  const [isPlo5, setPlo5] = useState<boolean>(sessionStorage.getItem('isPlo5') === 'true');
  const [isProfileMenuVisible, setProfileMenuVisible] = useState<boolean>(false);
  const accountButtonRef = useRef(null);
  const navigationRef = useRef(null);

  const { width } = useWindowDimensions();

  useEffect(() => {
    if (!isProfileMenuVisible && !isMenuVisible) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      const trigger = isProfileMenuVisible
        ? accountButtonRef.current : navigationRef.current?.querySelector('[data-header-menu="navigation"]');
      setProfileMenuVisible(false);
      setMenuVisible(false);
      if (trigger) trigger.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMenuVisible, isProfileMenuVisible]);

  useEffect(() => {
    if (width > 1120) setMenuVisible(false);
  }, [width]);

  useEffect(() => {
    function checkPlo5() {
      const item = sessionStorage.getItem('isPlo5');
      setPlo5(item === 'true');
    }

    window.addEventListener('storage', checkPlo5);

    return () => {
      window.removeEventListener('storage', checkPlo5);
    };
  }, []);

  const storageLang = localStorage.getItem('i18nextLng');
  // const storageL = (JSON.parse(localStorage.getItem('i18nextLng')).i18nextLng);

  const languageLink = () => {
    if (storageLang === 'ru') return 'ru';
    return 'en';
  };

  const applyPlo = (link: any) => (
    `${link}?isPlo5=${isPlo5}`
  );

  const toLink = (link: any) => {
    if (i18n.language.indexOf(`${languageLink()}`) >= 0 && link?.linkRu) return applyPlo(link?.linkRu);
    if (i18n.language.indexOf(`${languageLink()}`) >= 0 && link?.linkTo) return applyPlo(link?.linkTo);
    return applyPlo(link?.linkTo);
  };

  const logout = () => {
    dispatch({ type: actionTypes.LOGOUT });
    dispatch({ type: 'GET_PROFILE_INFO_CLEAR' });
    history.push(routes.app.root);
    toast.warn('You have logged out');
  };
  const [isToken, setToken] = useState<boolean>(false);
  useEffect(() => {
    setToken(!!localStorage.getItem('accessToken'));
  },
  [localStorage.getItem('accessToken')]);

  return (
    <>
      <section className={classnames(styles.container, className)}>
        {tutorialItemName ? <div className={styles.headerMask} /> : null}
        <OutsideClickHandler onOutsideClick={() => setMenuVisible(false)}>
          <div ref={navigationRef}>
            <MobileMenu
              isVisible={isMenuVisible}
              setVisible={setMenuVisible}
              isPlo5={isPlo5}
              onClose={() => {
                setMenuVisible(false);
                const trigger = navigationRef.current?.querySelector('[data-header-menu="navigation"]');
                if (trigger) trigger.focus();
              }}
            />
            <Burger
              className={styles.burgerBtn}
              isVisible={isMenuVisible}
              onClick={(visible) => {
                setProfileMenuVisible(false);
                setMenuVisible(visible);
              }}
            />
          </div>
        </OutsideClickHandler>
        <div id="mobile-page-settings" className={styles.pageSettings} />
        <Logo className={styles.logo} />
        <section
          className={styles.linkBlock}
        >
          <PloSwitch
            forHeader
          />
          {menuLinks.map((link) => (
            <LinkButton
              key={`${link.name}-${link.linkTo}`}
              name={t(link.name)}
              to={toLink(link)}
              external={link.external}
              onLanding={link.onLanding}
              // icon={link.icon ? link.icon : ''}
            >
              {link.icon && (
                <section className={styles.socialIconLink}>
                  <img src={link.icon} alt={t(link.name)} />
                </section>
              )}
            </LinkButton>
          ))}
        </section>
        <div className={styles.profile}>
          {isLoggedIn || isToken
            ? (
              <>
                <OutsideClickHandler
                  onOutsideClick={() => setProfileMenuVisible(false)}
                >
                  <div className={styles.profileMenuAnchor}>
                    <button
                      ref={accountButtonRef}
                      type="button"
                      aria-label="My Account"
                      aria-expanded={isProfileMenuVisible}
                      aria-controls="profile-menu"
                      data-header-menu="account"
                      className={classnames(
                        styles.accountButton,
                        { [styles.accountButtonOpen]: isProfileMenuVisible },
                      )}
                      onClick={() => {
                        setMenuVisible(false);
                        setProfileMenuVisible(!isProfileMenuVisible);
                      }}
                    >
                      <img
                        className={styles.accountIcon}
                        src={profileIcon}
                        alt=""
                      />
                      <span className={styles.accountLabel}>My Account</span>
                      <span
                        className={classnames(
                          styles.accountChevron,
                          { [styles.accountChevronOpen]: isProfileMenuVisible },
                        )}
                      />
                    </button>

                    <ProfileMenu
                      isVisible={isProfileMenuVisible}
                      setVisible={setProfileMenuVisible}
                      logout={logout}
                      isPlo5={isPlo5}
                    />
                  </div>
                </OutsideClickHandler>
              </>
            )
            : (
              <CustomLink
                name="LOG IN"
                use="secondary"
                to={routes.auth.signIn.root}
              />
            )}
          { width > 1120
            && <LanguageSelect />}
        </div>
      </section>
    </>
  );
};

export default Header;
